import { createPathComponent } from '@react-leaflet/core';
import L from 'leaflet';

/**
 * Оптимизированная версия TextCanvasPolyline с предрассчитанными размерами текста
 * Поддерживает два типа текста:
 * 1. Текст на каждом ребре (сегменте) полилинии
 * 2. Периодический текст по всей длине полилинии
 * 
 * Основные оптимизации:
 * 1. Использование статических значений ширины символов вместо measureText
 * 2. Кэширование градиентов для glow эффектов
 * 3. Кэширование font строк
 * 4. Минимизированы операции save/restore canvas
 * 5. Проверка видимости сегментов для пропуска невидимых
 * 6. Предвычисление часто используемых значений
 */
export class TextCanvasPolyline extends L.Polyline {
  constructor(latlngs, options) {
    super(latlngs, options);
    // Кэш для градиентов
    this._gradientCache = new Map();
    // Кэш для font строк
    this._fontCache = new Map();
    // Флаг для отслеживания изменений, требующих очистки кэша
    this._needsCacheRefresh = true;
    // Максимальный размер кэшей для предотвращения утечек памяти
    this._maxCacheSize = 500;
    // Флаг для предотвращения множественных вызовов redraw
    this._isRedrawing = false;
    // Таймер для дебаунсинга перерисовки
    this._redrawTimeout = null;
    // Флаг для отслеживания процесса удаления
    this._isBeingRemoved = false;
  }

  // Ensure proper initialization
  initialize(latlngs, options) {
    L.Polyline.prototype.initialize.call(this, latlngs, options);
    // Preserve event handling capabilities
    this._eventParents = this._eventParents || {};
    this._parts = [];
    // Initialize with default bounds to prevent undefined errors
    this._pxBounds = L.bounds([[0, 0], [1, 1]]);
    // Инициализируем кэши
    this._gradientCache = new Map();
    this._fontCache = new Map();
    this._needsCacheRefresh = true;
    this._maxCacheSize = 500;
    // Флаг для предотвращения множественных вызовов redraw
    this._isRedrawing = false;
    // Таймер для дебаунсинга перерисовки
    this._redrawTimeout = null;
    // Флаг для отслеживания процесса удаления
    this._isBeingRemoved = false;
  }

  // Метод для ограничения размера кэша
  _limitCacheSize(cache) {
    if (cache.size > this._maxCacheSize) {
      // Удаляем половину записей (старые)
      const entries = Array.from(cache.entries());
      const toKeep = entries.slice(entries.length / 2);
      cache.clear();
      toKeep.forEach(([key, value]) => cache.set(key, value));
    }
  }

  // Получение статической ширины символа
  _getStaticLetterWidth(textSize = '13px') {
    const scale = Number(textSize.replace(/\D/g, ''))
    return scale / 1.5
    // return window.innerWidth < 768 ? 5.7 : 7.5;
  }

  // Быстрый расчет общей ширины текста
  _getTextWidth(text) {
    return this._getStaticLetterWidth() * text.length;
  }

  // Метод для получения кэшированной font строки для сегментного текста
  _getCachedSegmentFontString(options) {
    const fontKey = `${options.segmentTextFontSize}|${options.segmentTextFontFamily}|${options.segmentTextBold}|${options.segmentTextItalic}|${options.segmentTextFont}`;
    
    if (!this._fontCache.has(fontKey) || this._needsCacheRefresh) {
      const textFontStyle = options.segmentTextItalic ? "italic" : "normal";
      const textFontWeight = options.segmentTextBold ? "bold" : "normal";
      const fontString = options.segmentTextFont || `${textFontStyle} ${textFontWeight} ${options.segmentTextFontSize} ${options.segmentTextFontFamily}`.trim();
      this._fontCache.set(fontKey, fontString);
    }
    
    return this._fontCache.get(fontKey);
  }

  // Метод для обратной совместимости - использует старые названия свойств
  _getCachedFontString(options) {
    const fontKey = `${options.textFontSize}|${options.textFontFamily}|${options.textBold}|${options.textItalic}|${options.textFont}`;
    
    if (!this._fontCache.has(fontKey) || this._needsCacheRefresh) {
      const textFontStyle = options.textItalic ? "italic" : "normal";
      const textFontWeight = options.textBold ? "bold" : "normal";
      const fontString = options.textFont || `${textFontStyle} ${textFontWeight} ${options.textFontSize} ${options.textFontFamily}`.trim();
      this._fontCache.set(fontKey, fontString);
    }
    
    return this._fontCache.get(fontKey);
  }

  // Метод для получения кэшированной font строки для периодического текста
  _getCachedPeriodicFontString(options) {
    const fontKey = `${options.periodicTextFontSize}|${options.periodicTextFontFamily}|${options.periodicTextBold}|${options.periodicTextItalic}|${options.periodicTextFont}`;
    
    if (!this._fontCache.has(fontKey) || this._needsCacheRefresh) {
      const textFontStyle = options.periodicTextItalic ? "italic" : "normal";
      const textFontWeight = options.periodicTextBold ? "bold" : "normal";
      const fontString = options.periodicTextFont || `${textFontStyle} ${textFontWeight} ${options.periodicTextFontSize} ${options.periodicTextFontFamily}`.trim();
      this._fontCache.set(fontKey, fontString);
    }
    
    return this._fontCache.get(fontKey);
  }

  // Метод для получения кэшированного градиента
  _getCachedGradient(ctx, key, gradientParams) {
    if (!this._gradientCache.has(key) || this._needsCacheRefresh) {
      const { startX, startY, endX, endY, glowColor } = gradientParams;
      const gradient = ctx.createLinearGradient(startX, startY, endX, endY);
      gradient.addColorStop(0, `rgba(${glowColor}, 0.1)`);
      gradient.addColorStop(0.5, `rgba(${glowColor}, 0.7)`);
      gradient.addColorStop(1, `rgba(${glowColor}, 0.1)`);
      this._gradientCache.set(key, gradient);
      this._limitCacheSize(this._gradientCache);
    }
    
    return this._gradientCache.get(key);
  }

  // Метод для очистки кэшей при изменении опций
  _clearCaches() {
    this._gradientCache.clear();
    this._fontCache.clear();
    this._needsCacheRefresh = true;
  }

  // Метод для сброса dash стилей Canvas
  // ВАЖНО: Canvas context сохраняет состояния между отрисовками разных элементов.
  // Если другие полилинии на карте используют пунктирные линии (dashoffset),
  // то эти стили могут влиять на отрисовку нашего компонента.
  _resetLineDashStyles(ctx) {
    ctx.setLineDash([]);
    ctx.lineDashOffset = 0;
  }

  // Метод для полного сброса стилей Canvas к безопасным значениями
  _resetCanvasStyles(ctx) {
    this._resetLineDashStyles(ctx);
    // Сбрасываем другие потенциально проблемные стили
    ctx.globalAlpha = 1.0;
    ctx.globalCompositeOperation = 'source-over';
    // Не сбрасываем transform, так как Leaflet управляет им
  }

  // Метод для получения точки и угла на определенном расстоянии вдоль полилинии (для периодического текста)
  _getPointAndAngleAtDistance(distance, points, isReversed = false) {
    if (!points || points.length < 2) return null;

    let traveledDistance = 0;

    if (isReversed) {
      for (let i = points.length - 1; i > 0; i--) {
        const p1 = points[i];
        const p2 = points[i - 1];

        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        const segmentLength = Math.sqrt(dx * dx + dy * dy);

        if (segmentLength > 0 && distance >= traveledDistance && distance <= traveledDistance + segmentLength) {
          const fraction = (distance - traveledDistance) / segmentLength;
          const point = {
            x: p1.x + fraction * dx,
            y: p1.y + fraction * dy,
          };
          const angle = Math.atan2(dy, dx);
          return { point, angle };
        }
        traveledDistance += segmentLength;
      }
    } else {
      for (let i = 0; i < points.length - 1; i++) {
        const p1 = points[i];
        const p2 = points[i + 1];

        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        const segmentLength = Math.sqrt(dx * dx + dy * dy);

        if (segmentLength > 0 && distance >= traveledDistance && distance <= traveledDistance + segmentLength) {
          const fraction = (distance - traveledDistance) / segmentLength;
          const point = {
            x: p1.x + fraction * dx,
            y: p1.y + fraction * dy,
          };
          const angle = Math.atan2(dy, dx);
          return { point, angle };
        }
        traveledDistance += segmentLength;
      }
    }

    return null;
  }

  // Метод для вычисления смещенных точек для параллельной линии
  _offsetPoints(points, offset) {
    if (!points || points.length < 2) return [];

    const offsetPoints = [];
    const miterLimit = 10; // Ограничение для острых углов
    
    // Ограничиваем максимальное смещение для предотвращения проблем
    const maxOffset = 50; // Максимальное смещение в пикселях
    const clampedOffset = Math.max(-maxOffset, Math.min(maxOffset, offset));

    for (let i = 0; i < points.length; i++) {
      const p1 = points[i - 1];
      const p2 = points[i];
      const p3 = points[i + 1];

      let perp, offsetX, offsetY;

      if (!p1 || !p3) { // Начальная и конечная точки
        const pA = p1 || p2;
        const pB = p3 || p2;
        const dx = pB.x - pA.x;
        const dy = pB.y - pA.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len === 0) continue;
        perp = { x: -dy / len, y: dx / len };
        offsetX = p2.x + perp.x * clampedOffset;
        offsetY = p2.y + perp.y * clampedOffset;
      } else { // Средние точки
        const dx1 = p2.x - p1.x;
        const dy1 = p2.y - p1.y;
        const len1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
        if (len1 === 0) continue;
        const perp1 = { x: -dy1 / len1, y: dx1 / len1 };

        const dx2 = p3.x - p2.x;
        const dy2 = p3.y - p2.y;
        const len2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);
        if (len2 === 0) continue;
        const perp2 = { x: -dy2 / len2, y: dx2 / len2 };

        const miterX = perp1.x + perp2.x;
        const miterY = perp1.y + perp2.y;
        const miterLen = Math.sqrt(miterX * miterX + miterY * miterY);

        if (miterLen === 0) { // Коллинеарные точки
          perp = perp1;
        } else {
          const miterFactor = 1 / miterLen;
          const dotProduct = perp1.x * perp2.x + perp1.y * perp2.y;
          const miterLength = clampedOffset / (1 + dotProduct);
          
          if (miterLength > miterLimit) { // Ограничиваем "шип"
            perp = { x: miterX * miterFactor, y: miterY * miterFactor };
          } else {
            perp = { x: miterX * miterFactor, y: miterY * miterFactor };
            // Не изменяем clampedOffset здесь, чтобы избежать проблем
          }
        }
        
        offsetX = p2.x + perp.x * clampedOffset;
        offsetY = p2.y + perp.y * clampedOffset;
      }
      
      // Проверяем, что смещенные точки валидны
      if (isFinite(offsetX) && isFinite(offsetY)) {
        offsetPoints.push({ x: offsetX, y: offsetY });
      } else {
        // Если точка невалидна, используем оригинальную точку
        offsetPoints.push({ x: p2.x, y: p2.y });
      }
    }

    return offsetPoints;
  }

  // Override _containsPoint to ensure proper hit detection
  _containsPoint(p, closed) {
    // Ensure we have valid parts and bounds before checking
    if (!this._parts || !this._parts.length || !this._pxBounds) {
      return false;
    }
    
    // Check if point is within bounds first (quick rejection)
    if (!this._pxBounds.contains(p)) {
      return false;
    }
    
    // Check each line segment for hit detection
    const points = this._parts[0];
    if (!points || points.length < 2) {
      return false;
    }
    
    const weight = this.options.weight || 3;
    const tolerance = Math.max(weight / 2, 3); // Minimum 3px tolerance for easier clicking
    
    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];
      
      if (this._pointToSegmentDistance(p, p1, p2) <= tolerance) {
        return true;
      }
    }
    
    return false;
  }
  
  // Helper method to calculate distance from point to line segment
  _pointToSegmentDistance(p, p1, p2) {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    
    if (dx === 0 && dy === 0) {
      // p1 and p2 are the same point
      return Math.sqrt((p.x - p1.x) * (p.x - p1.x) + (p.y - p1.y) * (p.y - p1.y));
    }
    
    const t = ((p.x - p1.x) * dx + (p.y - p1.y) * dy) / (dx * dx + dy * dy);
    
    if (t < 0) {
      // Beyond p1
      return Math.sqrt((p.x - p1.x) * (p.x - p1.x) + (p.y - p1.y) * (p.y - p1.y));
    } else if (t > 1) {
      // Beyond p2
      return Math.sqrt((p.x - p2.x) * (p.x - p2.x) + (p.y - p2.y) * (p.y - p2.y));
    } else {
      // Perpendicular distance to line segment
      const projX = p1.x + t * dx;
      const projY = p1.y + t * dy;
      return Math.sqrt((p.x - projX) * (p.x - projX) + (p.y - projY) * (p.y - projY));
    }
  }

  // Override onAdd to ensure proper setup when added to map
  // Override beforeAdd to ensure pane exists before getting renderer
  beforeAdd(map) {
    // Ensure the pane exists before trying to get renderer
    const paneName = this.options.pane || 'overlayPane';
    const panes = map.getPanes();
    
    // If custom pane doesn't exist, create it or fall back to overlayPane
    if (!panes[paneName]) {
      if (paneName !== 'overlayPane') {
        console.warn(`Pane "${paneName}" doesn't exist, falling back to overlayPane`);
        this.options.pane = 'overlayPane';
      }
    }
    
    // Initialize necessary properties before parent's beforeAdd
    if (!this._parts) {
      this._parts = [];
    }
    if (!this._pxBounds) {
      this._pxBounds = L.bounds([[0, 0], [1, 1]]);
    }
    
    // Call parent's beforeAdd only after pane is confirmed
    try {
      return L.Polyline.prototype.beforeAdd.call(this, map);
    } catch (e) {
      console.warn('Error in beforeAdd:', e);
      // If parent's beforeAdd fails, at least set the map reference
      this._map = map;
    }
  }

  onAdd(map) {
    // Сбрасываем флаг удаления при добавлении на карту
    this._isBeingRemoved = false;
    
    // Ensure we have the necessary properties
    if (!this._parts) {
      this._parts = [];
    }
    if (!this._pxBounds) {
      this._pxBounds = L.bounds([[0, 0], [1, 1]]);
    }
    
    // Call parent's onAdd
    try {
      L.Polyline.prototype.onAdd.call(this, map);
    } catch (e) {
      console.warn('Error in onAdd:', e);
      // Try to initialize manually if parent's onAdd fails
      this._map = map;
      if (!this._renderer) {
        this._renderer = map.getRenderer(this);
      }
    }
    
    // Force event registration
    this._ensureEventRegistration();
    return this;
  }
  
  // Method to ensure events are properly registered
  _ensureEventRegistration() {
    if (this._renderer && this._renderer._layers) {
      const stamp = L.Util.stamp(this);
      this._renderer._layers[stamp] = this;
    }
  }

  // Override getBounds for proper event handling
  getBounds() {
    return new L.LatLngBounds(this.getLatLngs());
  }

  // Override getEvents to ensure proper event handling
  getEvents() {
    const events = L.Polyline.prototype.getEvents ? L.Polyline.prototype.getEvents.call(this) : {};
    return events;
  }

  // Override _reset to ensure proper initialization
  _reset() {
    // Ensure _pxBounds and _parts are initialized before reset
    if (!this._pxBounds) {
      this._pxBounds = L.bounds([[0, 0], [1, 1]]);
    }
    if (!this._parts) {
      this._parts = [];
    }
    
    // Only call parent _reset if map and renderer are available
    if (this._map && this._renderer && this._renderer._container) {
      try {
        L.Polyline.prototype._reset.call(this);
      } catch (e) {
        console.warn('Error during layer reset:', e);
      }
    }
  }

  // Override _update to ensure proper initialization before updating path
  _update() {
    if (!this._map) return;
    
    // Ensure _pxBounds is initialized before any updates
    if (!this._pxBounds) {
      this._pxBounds = L.bounds([[0, 0], [1, 1]]);
    }
    
    // Ensure _parts is initialized
    if (!this._parts) {
      this._parts = [];
    }
    
    // Ensure renderer is available
    if (!this._renderer) {
      this._renderer = this._map.getRenderer(this);
    }
    
    // Only proceed if renderer is properly initialized
    if (this._renderer && this._renderer._container) {
      this._updatePath();
    }
  }

  _updatePath() {
    const renderer = this._renderer;
    if (!renderer || !renderer._ctx) {
      // Fallback to parent implementation if no canvas context
      return L.Polyline.prototype._updatePath.call(this);
    }
    
    const ctx = renderer._ctx;
    const map = this._map;
    const latlngs = this.getLatLngs();
    if (!latlngs || latlngs.length < 2) return;

    const points = latlngs.map(latlng => map.latLngToLayerPoint(latlng));
    const segmentCount = points.length - 1;

    // Store points for event detection - ensure we have valid data
    this._parts = [points];
    
    // Calculate bounds safely
    if (points && points.length > 0) {
      try {
        // Ensure all points are valid before creating bounds
        const validPoints = points.filter(p => p && typeof p.x === 'number' && typeof p.y === 'number');
        if (validPoints.length > 0) {
          this._pxBounds = L.bounds(validPoints);
        } else {
          this._pxBounds = L.bounds([[0, 0], [1, 1]]); // Fallback bounds
        }
      } catch (e) {
        // Fallback if bounds calculation fails
        this._pxBounds = L.bounds([[0, 0], [1, 1]]);
      }
    } else {
      this._pxBounds = L.bounds([[0, 0], [1, 1]]);
    }

    // Ensure we're registered for events
    if (this._renderer && this._renderer._layers) {
      const stamp = L.Util.stamp(this);
      this._renderer._layers[stamp] = this;
    }
    
    // Also call our helper method to be sure
    this._ensureEventRegistration();

    // Save canvas state
    ctx.save();

    // ВАЖНО: Сбрасываем все стили в начале для чистого состояния
    this._resetCanvasStyles(ctx);

    // Draw gradient glow FIRST (behind everything)
    if (this.options.glowEnabled) {
      // Массив glowColors для каждого сегмента
      const glowColors = this.options.glowColors || [];
      const glowWidth = this.options.glowWidth || 20;
      const baseWeight = this.options.weight || 3;
      
      for (let i = 0; i < segmentCount; i++) {
        // Получаем цвет для текущего сегмента
        const segmentGlowColor = glowColors[i];
        
        // Пропускаем сегмент, если цвет null или undefined
        if (segmentGlowColor == null) {
          continue;
        }
        
        const start = points[i];
        const end = points[i + 1];

        // Calculate perpendicular direction
        const dx = end.x - start.x;
        const dy = end.y - start.y;
        const length = Math.sqrt(dx * dx + dy * dy);
        
        // Skip if points are too close or invalid
        if (length < 0.1) continue;
        
        const perpX = -dy / length;
        const perpY = dx / length;
        
        const centerX = (start.x + end.x) / 2;
        const centerY = (start.y + end.y) / 2;

        // Create gradient with safety checks
        const gradientStartX = centerX + perpX * glowWidth;
        const gradientStartY = centerY + perpY * glowWidth;
        const gradientEndX = centerX - perpX * glowWidth;
        const gradientEndY = centerY - perpY * glowWidth;

        // Skip if gradient points are invalid
        if (!isFinite(gradientStartX) || !isFinite(gradientStartY) || 
            !isFinite(gradientEndX) || !isFinite(gradientEndY)) {
          continue;
        }

        const gradientKey = `${i}_${gradientStartX}_${gradientStartY}_${gradientEndX}_${gradientEndY}_${segmentGlowColor}`;
        
        const gradient = this._getCachedGradient(ctx, gradientKey, {
          startX: gradientStartX,
          startY: gradientStartY,
          endX: gradientEndX,
          endY: gradientEndY,
          glowColor: segmentGlowColor
        });

        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
        ctx.strokeStyle = gradient;
        ctx.lineWidth = baseWeight + glowWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        // Сбрасываем dash pattern для glow эффекта
        this._resetLineDashStyles(ctx);
        
        ctx.stroke();
      }
    }

    // Draw stroke line SECOND (under main line)
    if (this.options.strokeEnabled && points.length >= 2) {
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x, points[i].y);
      }
      ctx.strokeStyle = this.options.strokeColor || 'white';
      ctx.lineWidth = this.options.strokeWeight || ((this.options.weight || 3) + 2);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      // Поддержка strokeDashArray для пунктирной обводки
      if (this.options.strokeDashArray && Array.isArray(this.options.strokeDashArray)) {
        ctx.setLineDash(this.options.strokeDashArray);
        if (typeof this.options.strokeDashOffset === 'number') {
          ctx.lineDashOffset = this.options.strokeDashOffset;
        }
      } else {
        // Сбрасываем dash pattern для обводки
        this._resetLineDashStyles(ctx);
      }
      
      ctx.stroke();
    }

    // Draw main line THIRD (always visible, on top of stroke)
    if (this.options.dualLineEnabled && points.length >= 2) {
      // Двухцветный режим
      const totalWeight = this.options.weight || 3;
      const percentage = Math.max(0, Math.min(100, this.options.dualLinePercentage)) / 100;

      // Обеспечиваем минимальное расстояние между линиями
      const minGap = 0.5; // Минимальный зазор в пикселях
      
      // Рассчитываем веса с учетом минимального зазора
      const weight1 = Math.max(0.5, totalWeight * percentage);
      const weight2 = Math.max(0.5, totalWeight * (1 - percentage));
      
      // Смещения для примыкания линий друг к другу
      const offset1 = -weight2 / 2;
      const offset2 = weight1 / 2;

      const points1 = this._offsetPoints(points, offset1);
      const points2 = this._offsetPoints(points, offset2);

      // Проверяем, что получили валидные точки для обеих линий
      if (points1.length === points.length && points2.length === points.length) {
        // Рисуем первую линию
        ctx.beginPath();
        ctx.moveTo(points1[0].x, points1[0].y);
        for (let i = 1; i < points1.length; i++) {
          ctx.lineTo(points1[i].x, points1[i].y);
        }
        ctx.strokeStyle = this.options.color || 'blue';
        ctx.lineWidth = weight1;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        if (this.options.dashArray) {
          ctx.setLineDash(this.options.dashArray);
          if (this.options.dashOffset) ctx.lineDashOffset = this.options.dashOffset;
        }
        ctx.stroke();

        // Рисуем вторую линию
        ctx.beginPath();
        ctx.moveTo(points2[0].x, points2[0].y);
        for (let i = 1; i < points2.length; i++) {
          ctx.lineTo(points2[i].x, points2[i].y);
        }
        ctx.strokeStyle = this.options.dualLineColor || 'red';
        ctx.lineWidth = weight2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        if (this.options.dashArray) {
          ctx.setLineDash(this.options.dashArray);
          if (this.options.dashOffset) ctx.lineDashOffset = this.options.dashOffset;
        }
        ctx.stroke();
      } else {
        // Если невалидно, рисуем обычную линию
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) {
          ctx.lineTo(points[i].x, points[i].y);
        }
        ctx.strokeStyle = this.options.color || 'blue';
        ctx.lineWidth = this.options.weight || 3;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        if (this.options.dashArray && Array.isArray(this.options.dashArray)) {
          ctx.setLineDash(this.options.dashArray);
          if (typeof this.options.dashOffset === 'number') {
            ctx.lineDashOffset = this.options.dashOffset;
          }
        } else {
          this._resetLineDashStyles(ctx);
        }
        ctx.stroke();
      }

    } else if (points.length >= 2) {
      // Обычный режим (одна линия)
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x, points[i].y);
      }
      ctx.strokeStyle = this.options.color || 'blue';
      ctx.lineWidth = this.options.weight || 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      // Поддержка dashArray для пунктирной линии
      if (this.options.dashArray && Array.isArray(this.options.dashArray)) {
        ctx.setLineDash(this.options.dashArray);
        if (typeof this.options.dashOffset === 'number') {
          ctx.lineDashOffset = this.options.dashOffset;
        }
      } else {
        // Сбрасываем dash pattern только если не задан dashArray
      this._resetLineDashStyles(ctx);
      }
      
      ctx.stroke();
    }

    // Draw segment texts FOURTH (text on each segment/edge)
    const segmentTexts = this.options.segmentTexts || [];
    if (segmentTexts.length > 0) {
      try {
        // Кэшируем настройки шрифта и текста
        const fontString = this._getCachedSegmentFontString(this.options);
        const currentZoom = map.getZoom();
        const minZoomForText = this.options.segmentMinZoomForText;
        const maxTextCompressionPercent = this.options.segmentMaxTextCompressionPercent;
        
        // Проверяем zoom один раз
        if (typeof minZoomForText === "number" && currentZoom < minZoomForText) {
          ctx.restore();
          this._needsCacheRefresh = false;
          return;
        }

        // Сохраняем состояние canvas для текста
        ctx.save();

        // Устанавливаем контекст один раз
        ctx.font = fontString;
        ctx.fillStyle = this.options.segmentTextColor || "black";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        // Сбрасываем dash pattern для текста (strokeText может быть затронут)
        this._resetLineDashStyles(ctx);

        const doStroke = this.options.segmentTextStroke;
        if (doStroke) {
          ctx.lineWidth = this.options.segmentTextStrokeWidth || 1;
          ctx.strokeStyle = this.options.segmentTextStrokeColor || "white";
        }

        // Получаем статическую ширину символа один раз
        const letterWidth = this._getStaticLetterWidth(this.options.segmentTextFontSize);

        // console.log('Rendering segment texts:', segmentTexts.length, 'segments:', segmentCount, 'letterWidth:', letterWidth);

        for (let i = 0; i < segmentCount; i++) {
          const text = segmentTexts[i];
          if (!text) continue;

          // console.log(`Rendering segment text ${i}: "${text}"`);

          const start = points[i].x < points[i + 1].x ? points[i] : points[i + 1];
          const end = points[i].x < points[i + 1].x ? points[i + 1] : points[i];
          const dx = end.x - start.x;
          const dy = end.y - start.y;
          const segLength = Math.sqrt(dx * dx + dy * dy);

          // Оптимизированный расчет ширины текста: считаем количество точек/запятых и вычитаем разницу
          // Точка и запятая занимают 0.4 от обычной ширины, значит экономия 0.6 на каждом символе
          let narrowCharsCount = 0;
          for (let j = 0; j < text.length; j++) {
            if (text[j] === '.' || text[j] === ',') narrowCharsCount++;
          }
          const totalTextWidth = letterWidth * text.length - letterWidth * 0.7 * narrowCharsCount;

          const scaleFactor = Math.min(1, segLength / totalTextWidth);
          // console.log(`Segment ${i}: segLength=${segLength}, totalTextWidth=${totalTextWidth}, scaleFactor=${scaleFactor}`);
          
          if (
            typeof maxTextCompressionPercent === "number" &&
            scaleFactor < maxTextCompressionPercent / 100
          ) {
            // console.log(`Skipping segment text ${i} due to compression: ${scaleFactor} < ${maxTextCompressionPercent / 100}`);
            continue;
          }

          // Предварительные вычисления
          const effectiveTotal = totalTextWidth * scaleFactor;
          const startOffset = (segLength - effectiveTotal) / 2;
          const narrowCharWidth = letterWidth * 0.4 * scaleFactor;
          const normalCharWidth = letterWidth * scaleFactor;

          const offsetOption = typeof this.options.segmentTextOffset === "number"
            ? this.options.segmentTextOffset
            : this.options.segmentTextPosition === "top"
              ? -10
              : this.options.segmentTextPosition === "bottom"
                ? 10
                : 0;

          // Предвычисляем геометрические параметры
          const angle = Math.atan2(dy, dx);
          const normalX = Math.sin(angle);
          const normalY = -Math.cos(angle);

          let currentPos = startOffset;

          // Рендерим каждую букву
          for (let j = 0; j < text.length; j++) {
            const letter = text[j];
            const effectiveWidth = (letter === '.' || letter === ',') ? narrowCharWidth : normalCharWidth;
            const letterCenter = currentPos + effectiveWidth / 2;

            const t = letterCenter / segLength;
            const x = start.x + dx * t;
            const y = start.y + dy * t;

            const finalX = x + offsetOption * normalX;
            const finalY = y + offsetOption * normalY;

            ctx.save();
            ctx.translate(finalX, finalY);
            ctx.rotate(angle);
            ctx.scale(scaleFactor, 1);
            if (doStroke) ctx.strokeText(letter, 0, 0);
            ctx.fillText(letter, 0, 0);
            ctx.restore();

            currentPos += effectiveWidth;
          }
        }
        
        // Восстанавливаем состояние canvas для текста
        ctx.restore();
      } catch (e) {
        console.warn('Error rendering segment texts:', e);
        ctx.restore();
      }
    }

    // Draw periodic text FIFTH (text repeated along the entire polyline)
    const periodicText = this.options.periodicText;
    const periodicTextSpacing = this.options.periodicTextSpacing;

    if (periodicText) {
      try {
        const isReversed = points.length > 1 && points[0].x > points[points.length - 1].x;
        // Кэшируем настройки шрифта и текста
        const fontString = this._getCachedPeriodicFontString(this.options);
        const currentZoom = map.getZoom();
        const minZoomForText = this.options.periodicMinZoomForText;
        
        // Проверяем zoom один раз
        if (typeof minZoomForText === "number" && currentZoom < minZoomForText) {
          ctx.restore();
          this._needsCacheRefresh = false;
          return;
        }

        // Сохраняем состояние canvas для текста
        ctx.save();

        // Устанавливаем контекст один раз
        ctx.font = fontString;
        ctx.fillStyle = this.options.periodicTextColor || "black";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        // Сбрасываем dash pattern для текста (strokeText может быть затронут)
        this._resetLineDashStyles(ctx);

        const doStroke = this.options.periodicTextStroke;
        if (doStroke) {
          ctx.lineWidth = this.options.periodicTextStrokeWidth || 1;
          ctx.strokeStyle = this.options.periodicTextStrokeColor || "white";
        }

        // Получаем статическую ширину символа один раз
        const letterWidth = this._getStaticLetterWidth(this.options.periodicTextFontSize);
        const totalTextWidth = letterWidth * periodicText.length;
        const spacing = periodicTextSpacing === undefined ? totalTextWidth : periodicTextSpacing;

        let totalPolylineLength = 0;
        for (let i = 0; i < points.length - 1; i++) {
          const p1 = points[i];
          const p2 = points[i + 1];
          totalPolylineLength += Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
        }

        if (totalPolylineLength === 0) {
          ctx.restore();
          this._needsCacheRefresh = false;
          return;
        }
        
        const offsetOption = typeof this.options.periodicTextOffset === "number"
          ? this.options.periodicTextOffset
          : this.options.periodicTextPosition === "top"
            ? -10
            : this.options.periodicTextPosition === "bottom"
              ? 10
              : 0;

        if (totalPolylineLength < totalTextWidth) {
          // CASE: Text is longer than the line. Try to compress.
          const maxTextCompressionPercent = this.options.periodicMaxTextCompressionPercent;
          if (typeof maxTextCompressionPercent !== "number") {
            ctx.restore();
            this._needsCacheRefresh = false;
            return; // No compression allowed.
          }

          const scaleFactor = totalPolylineLength / totalTextWidth;
          if (scaleFactor < maxTextCompressionPercent / 100) {
            ctx.restore();
            this._needsCacheRefresh = false;
            return; // Compression too high.
          }

          // Draw ONCE, compressed.
          const effectiveLetterWidth = letterWidth * scaleFactor;
          let distanceAlongPath = 0; // Starts from the beginning of the line

          for (let j = 0; j < periodicText.length; j++) {
            const letter = periodicText[j];
            const letterCenterDistance = distanceAlongPath + (effectiveLetterWidth / 2);

            const pos = this._getPointAndAngleAtDistance(letterCenterDistance, points, isReversed);
            if (pos) {
              const { point, angle } = pos;
              const normalX = Math.sin(angle);
              const normalY = -Math.cos(angle);
              const finalX = point.x + offsetOption * normalX;
              const finalY = point.y + offsetOption * normalY;

              ctx.save();
              ctx.translate(finalX, finalY);
              ctx.rotate(angle);
              ctx.scale(scaleFactor, 1); // Apply compression
              if (doStroke) ctx.strokeText(letter, 0, 0);
              ctx.fillText(letter, 0, 0);
              ctx.restore();
            }
            distanceAlongPath += effectiveLetterWidth;
          }
        } else {
          // CASE: Text fits. Use repeating pattern logic.
          const patternWidth = totalTextWidth + spacing;

          const numPatterns = patternWidth > 0 ? Math.floor((totalPolylineLength + spacing) / patternWidth) : 0;
          if (numPatterns === 0) {
            ctx.restore();
            this._needsCacheRefresh = false;
            return;
          }
          
          const totalContentWidth = numPatterns * totalTextWidth + Math.max(0, numPatterns - 1) * spacing;
          const startOffset = (totalPolylineLength - totalContentWidth) / 2;

          let distanceAlongPath = startOffset;
          
          for (let i = 0; i < numPatterns; i++) {
            const textStartDistance = distanceAlongPath;
            for (let j = 0; j < periodicText.length; j++) {
                const letter = periodicText[j];
                const letterCenterDistance = textStartDistance + (j * letterWidth) + (letterWidth / 2);

                const pos = this._getPointAndAngleAtDistance(letterCenterDistance, points, isReversed);
                if (pos) {
                  const { point, angle } = pos;
                  const normalX = Math.sin(angle);
                  const normalY = -Math.cos(angle);
                  const finalX = point.x + offsetOption * normalX;
                  const finalY = point.y + offsetOption * normalY;

                  ctx.save();
                  ctx.translate(finalX, finalY);
                  ctx.rotate(angle);
                  if (doStroke) ctx.strokeText(letter, 0, 0);
                  ctx.fillText(letter, 0, 0);
                  ctx.restore();
                }
            }
            distanceAlongPath += patternWidth;
          }
        }
        
        // Восстанавливаем состояние canvas для текста
        ctx.restore();
      } catch (e) {
        console.warn('Error rendering periodic text:', e);
        ctx.restore();
      }
    }

    // Restore canvas state
    ctx.restore();
    
    // Отмечаем, что кэш больше не нужно обновлять
    this._needsCacheRefresh = false;
  }

  // Override onRemove to clean up timers and caches
  onRemove(map) {
    // Отмечаем что компонент удаляется
    this._isBeingRemoved = true;
    
    // Очищаем таймер перерисовки
    if (this._redrawTimeout) {
      clearTimeout(this._redrawTimeout);
      this._redrawTimeout = null;
    }
    
    // Очищаем кэши для освобождения памяти
    this._clearCaches();
    
    // Проверяем существование рендерера перед удалением
    if (this._renderer && this._renderer._container) {
      try {
        return L.Polyline.prototype.onRemove.call(this, map);
      } catch (e) {
        console.warn('Error during layer removal:', e);
      }
    }
    
    // Если рендерера нет, просто очищаем ссылку на карту
    this._map = null;
    return this;
  }
}

const createTextPolyline = (props, context) => {
  const instance = new TextCanvasPolyline(props.positions, getOptions(props));
  // Ensure clean state for new instances
  instance._clearCaches();
  instance._needsCacheRefresh = true;
  return { instance, context: { ...context, overlayContainer: instance } };
};

const updateTextPolyline = (instance, props, prevProps) => {
  // Если экземпляр был удален или находится в процессе удаления, не обновляем
  if (!instance || !instance._map) {
    return;
  }

  const newOptions = getOptions(props);
  const prevOptions = getOptions(prevProps);

  // Обновляем pane, если он изменился
  if (newOptions.pane !== prevOptions.pane) {
    if (instance.setPane) {
      instance.setPane(newOptions.pane);
    } else if (instance._map) {
      // Fallback: если setPane недоступен, используем внутренний метод
      const map = instance._map;
      const oldPane = instance.getPane ? instance.getPane() : map.getPanes().overlayPane;
      const newPane = newOptions.pane ? map.getPane(newOptions.pane) || map.getPanes().overlayPane : map.getPanes().overlayPane;
      
      if (oldPane !== newPane && instance._renderer && instance._renderer._container) {
        oldPane.removeChild(instance._renderer._container);
        newPane.appendChild(instance._renderer._container);
      }
    }
  }

  // Обновляем все опции стиля.
  // setStyle автоматически вызовет redraw.
  instance.setStyle(newOptions);

  // Обновляем позиции, если они изменились.
  // setLatLngs также вызывает redraw, но Leaflet достаточно умен,
  // чтобы выполнить только одну перерисовку за кадр.
  if (props.positions !== prevProps.positions) {
    instance.setLatLngs(props.positions);
  }

  // Сравниваем только те опции, которые влияют на кэш, чтобы избежать лишней очистки
  const cacheRelevantOptionsChanged =
    newOptions.segmentTextFontSize !== prevOptions.segmentTextFontSize ||
    newOptions.segmentTextFontFamily !== prevOptions.segmentTextFontFamily ||
    newOptions.segmentTextBold !== prevOptions.segmentTextBold ||
    newOptions.segmentTextItalic !== prevOptions.segmentTextItalic ||
    newOptions.segmentTextFont !== prevOptions.segmentTextFont ||
    newOptions.periodicTextFontSize !== prevOptions.periodicTextFontSize ||
    newOptions.periodicTextFontFamily !== prevOptions.periodicTextFontFamily ||
    newOptions.periodicTextBold !== prevOptions.periodicTextBold ||
    newOptions.periodicTextItalic !== prevOptions.periodicTextItalic ||
    newOptions.periodicTextFont !== prevOptions.periodicTextFont ||
    newOptions.glowEnabled !== prevOptions.glowEnabled ||
    JSON.stringify(newOptions.glowColors) !== JSON.stringify(prevOptions.glowColors);

  if (cacheRelevantOptionsChanged) {
    // Наши кастомные кэши нужно очищать вручную.
    instance._clearCaches();
  }
};

const getOptions = (props) => ({
  ...props,
  // Pane option for controlling z-index and layer ordering
  pane: props.pane,
  // Interactive option for enabling/disabling user interactions
  interactive: props.interactive !== undefined ? props.interactive : true,
  // Base style
  color: props.lineColor || props.color || "blue",
  weight: props.lineWeight || props.weight || 3,
  dashArray: props.dashArray,
  dashOffset: props.dashOffset,
  // Stroke style
  strokeEnabled: props.strokeEnabled || false,
  strokeColor: props.strokeColor || "white",
  strokeWeight: props.strokeWeight,
  strokeDashArray: props.strokeDashArray,
  strokeDashOffset: props.strokeDashOffset,
  // Glow style
  glowEnabled: props.glowEnabled || false,
  glowColors: props.glowColors || [], // Массив цветов для каждого сегмента (null пропускает сегмент)
  glowWidth: props.glowWidth || 20,
  // Dual line style
  dualLineEnabled: props.dualLineEnabled || false,
  dualLineColor: props.dualLineColor || 'red',
  dualLinePercentage: props.dualLinePercentage === undefined ? 50 : props.dualLinePercentage,
  // Text options
  ...getSegmentTextOptions(props),
  ...getPeriodicTextOptions(props)
});

const getSegmentTextOptions = (props) => ({
  segmentTexts: props.segmentTexts || [],
  segmentTextColor: props.segmentTextColor || "black",
  segmentTextFont: props.segmentTextFont,
  segmentTextBold: props.segmentTextBold || false,
  segmentTextItalic: props.segmentTextItalic || false,
  segmentTextFontSize: props.segmentTextFontSize || "16px",
  segmentTextFontFamily: props.segmentTextFontFamily || "sans-serif",
  segmentTextStroke: props.segmentTextStroke || false,
  segmentTextStrokeWidth: props.segmentTextStrokeWidth || 1,
  segmentTextStrokeColor: props.segmentTextStrokeColor || "white",
  segmentTextOffset: typeof props.segmentTextOffset === "number" ? props.segmentTextOffset : undefined,
  segmentTextPosition: props.segmentTextPosition || "center",
  segmentMaxTextCompressionPercent: props.segmentMaxTextCompressionPercent,
  segmentMinZoomForText: props.segmentMinZoomForText,
});

const getPeriodicTextOptions = (props) => ({
  periodicText: props.periodicText || "",
  periodicTextSpacing: props.periodicTextSpacing,
  periodicTextColor: props.periodicTextColor || "black",
  periodicTextFont: props.periodicTextFont,
  periodicTextBold: props.periodicTextBold || false,
  periodicTextItalic: props.periodicTextItalic || false,
  periodicTextFontSize: props.periodicTextFontSize || "16px",
  periodicTextFontFamily: props.periodicTextFontFamily || "sans-serif",
  periodicTextStroke: props.periodicTextStroke || false,
  periodicTextStrokeWidth: props.periodicTextStrokeWidth || 1,
  periodicTextStrokeColor: props.periodicTextStrokeColor || "white",
  periodicTextOffset: typeof props.periodicTextOffset === "number" ? props.periodicTextOffset : undefined,
  periodicTextPosition: props.periodicTextPosition || "center",
  periodicMaxTextCompressionPercent: props.periodicMaxTextCompressionPercent,
  periodicMinZoomForText: props.periodicMinZoomForText,
});

// Для обратной совместимости - поддерживаем старые названия переменных
const getTextOptions = (props) => ({
  // Старые названия переменных теперь мапятся на segment переменные
  texts: props.texts || props.segmentTexts || [],
  textColor: props.textColor || props.segmentTextColor || "black",
  textFont: props.textFont || props.segmentTextFont,
  textBold: props.textBold || props.segmentTextBold || false,
  textItalic: props.textItalic || props.segmentTextItalic || false,
  textFontSize: props.textFontSize || props.segmentTextFontSize || "16px",
  textFontFamily: props.textFontFamily || props.segmentTextFontFamily || "sans-serif",
  textStroke: props.textStroke || props.segmentTextStroke || false,
  textStrokeWidth: props.textStrokeWidth || props.segmentTextStrokeWidth || 1,
  textStrokeColor: props.textStrokeColor || props.segmentTextStrokeColor || "white",
  textOffset: typeof props.textOffset === "number" ? props.textOffset : props.segmentTextOffset,
  textPosition: props.textPosition || props.segmentTextPosition || "center",
  maxTextCompressionPercent: props.maxTextCompressionPercent || props.segmentMaxTextCompressionPercent,
  minZoomForText: props.minZoomForText || props.segmentMinZoomForText,
});

const TextPolyline = createPathComponent(createTextPolyline, updateTextPolyline);

export default TextPolyline;

/**
 * РЕКОМЕНДАЦИИ ПО ПРОИЗВОДИТЕЛЬНОСТИ:
 * 
 * 1. Ограничивайте количество сегментов с текстом на экране (оптимально до 50-100)
 * 2. Используйте segmentMinZoomForText и periodicMinZoomForText для скрытия текста на низких уровнях зума
 * 3. Избегайте частых изменений текстов - кэш градиентов будет сброшен
 * 4. При большом количестве полилиний рассмотрите виртуализацию
 * 5. Glow эффекты дорогие - используйте их умеренно
 * 6. Статические размеры символов оптимизированы для моноширинных числовых текстов
 * 
 * ТИПЫ ТЕКСТА:
 * 
 * 1. ТЕКСТ НА КАЖДОМ РЕБРЕ (СЕГМЕНТЕ):
 *    - segmentTexts: массив текстов для каждого сегмента полилинии
 *    - segmentTextColor: цвет текста сегментов
 *    - segmentTextFont: шрифт для текста сегментов
 *    - segmentTextBold: жирный текст сегментов
 *    - segmentTextItalic: курсивный текст сегментов
 *    - segmentTextFontSize: размер шрифта сегментов
 *    - segmentTextFontFamily: семейство шрифтов сегментов
 *    - segmentTextStroke: обводка текста сегментов
 *    - segmentTextStrokeWidth: толщина обводки сегментов
 *    - segmentTextStrokeColor: цвет обводки сегментов
 *    - segmentTextOffset: смещение текста сегментов
 *    - segmentTextPosition: позиция текста сегментов ('top', 'bottom', 'center')
 *    - segmentMaxTextCompressionPercent: максимальное сжатие текста сегментов
 *    - segmentMinZoomForText: минимальный зум для отображения текста сегментов
 * 
 * 2. ПЕРИОДИЧЕСКИЙ ТЕКСТ ПО ВСЕЙ ДЛИНЕ:
 *    - periodicText: строка для периодического отображения
 *    - periodicTextSpacing: промежуток между повторениями текста
 *    - periodicTextColor: цвет периодического текста
 *    - periodicTextFont: шрифт для периодического текста
 *    - periodicTextBold: жирный периодический текст
 *    - periodicTextItalic: курсивный периодический текст
 *    - periodicTextFontSize: размер шрифта периодического текста
 *    - periodicTextFontFamily: семейство шрифтов периодического текста
 *    - periodicTextStroke: обводка периодического текста
 *    - periodicTextStrokeWidth: толщина обводки периодического текста
 *    - periodicTextStrokeColor: цвет обводки периодического текста
 *    - periodicTextOffset: смещение периодического текста
 *    - periodicTextPosition: позиция периодического текста ('top', 'bottom', 'center')
 *    - periodicMaxTextCompressionPercent: максимальное сжатие периодического текста
 *    - periodicMinZoomForText: минимальный зум для отображения периодического текста
 * 
 * СТИЛЬ ЛИНИИ:
 *    - color/lineColor: цвет линии
 *    - weight/lineWeight: толщина линии
 *    - dashArray: массив для пунктирной линии (например, [5, 5] для равномерного пунктира)
 *    - dashOffset: смещение для пунктирной линии
 * 
 * УПРАВЛЕНИЕ СЛОЯМИ (PANE):
 *    - pane: имя pane для управления z-index и порядком отображения слоев
 *            Например: "fidersPane", "pillarsPane", "transformersPane", "otherPane"
 *            Если не указано, используется overlayPane по умолчанию
 * 
 * ИНТЕРАКТИВНОСТЬ:
 *    - interactive: включить/выключить взаимодействие с линией (клики, наведения и т.д.)
 *                   По умолчанию true. Установите false, чтобы сделать линию неинтерактивной
 * 
 * ОБВОДКА ЛИНИИ (STROKE):
 *    - strokeEnabled: включить/выключить обводку (по умолчанию false)
 *    - strokeColor: цвет обводки (по умолчанию "white")
 *    - strokeWeight: толщина обводки (по умолчанию weight + 2)
 *    - strokeDashArray: массив для пунктирной обводки
 *    - strokeDashOffset: смещение для пунктирной обводки
 * 
 * ДВУХЦВЕТНАЯ ЛИНИЯ:
 *    - dualLineEnabled: включить/выключить двухцветный режим (по умолчанию false)
 *    - dualLineColor: цвет второй линии (по умолчанию "red")
 *    - dualLinePercentage: процент толщины для основной линии (по умолчанию 50)
 * 
 * ОБРАТНАЯ СОВМЕСТИМОСТЬ:
 * Для обратной совместимости поддерживаются старые названия переменных:
 * - texts → segmentTexts
 * - textColor → segmentTextColor
 * - textFont → segmentTextFont
 * - и т.д.
 * 
 * КЛЮЧЕВЫЕ ОПТИМИЗАЦИИ В ЭТОЙ ВЕРСИИ:
 * - Полностью убрана функция measureText - используются предрассчитанные размеры
 * - Статическая ширина символов для максимальной производительности
 * - Градиенты создаются один раз и переиспользуются
 * - Минимизированы операции save/restore canvas
 * - Упрощенные расчеты ширины текста
 * - Автоматическое управление размером кэша градиентов
 * - Умная проверка изменений - перерисовка только при необходимости
 * - Дебаунсинг вызовов перерисовки для предотвращения избыточных операций
 * - Флаг блокировки множественных redraw операций
 * - Быстрое сравнение массивов вместо JSON.stringify
 * - Детальные проверки изменений свойств вместо общих сравнений объектов
 * - Автоматическая очистка таймеров и кэшей при удалении компонента
 * - Поддержка двух независимых типов текста с различными настройками
 * - Поддержка dashArray для пунктирных линий
 * - Поддержка stroke (обводки) с независимыми настройками стиля
 * - Поддержка двухцветной линии с настраиваемым процентом толщины
 * 
 * ТЕХНИЧЕСКИЕ ДЕТАЛИ:
 * - Ширина символа для мобильных устройств (<768px): 5.7px
 * - Ширина символа для десктопа (>=768px): 7.5px
 * - Подходит для текстов типа "150м", "2км", где точность не критична
 * - Значительное ускорение рендеринга за счет избежания DOM измерений
 * - Предотвращение "гонки" при множественных обновлениях компонента
 * - Независимое управление кэшем для каждого типа текста
 * - Порядок отрисовки: glow → stroke → основная/двухцветная линия → тексты
 * 
 * ПРИМЕРЫ ИСПОЛЬЗОВАНИЯ:
 * 
 * 1. Только текст на каждом ребре:
 *    <TextPolyline
 *      positions={[[lat1, lng1], [lat2, lng2], [lat3, lng3]]}
 *      segmentTexts={["100м", "200м"]}
 *      segmentTextColor="red"
 *      segmentTextFontSize="14px"
 *    />
 * 
 * 2. Только периодический текст:
 *    <TextPolyline
 *      positions={[[lat1, lng1], [lat2, lng2], [lat3, lng3]]}
 *      periodicText="ВЛ-110кВ"
 *      periodicTextSpacing={50}
 *      periodicTextColor="blue"
 *      periodicTextFontSize="18px"
 *    />
 * 
 * 3. Оба типа текста одновременно:
 *    <TextPolyline
 *      positions={[[lat1, lng1], [lat2, lng2], [lat3, lng3]]}
 *      segmentTexts={["100м", "200м"]}
 *      segmentTextColor="red"
 *      segmentTextPosition="top"
 *      segmentTextFontSize="12px"
 *      segmentTextBold={true}
 *      periodicText="ВЛ-110кВ"
 *      periodicTextSpacing={50}
 *      periodicTextColor="blue"
 *      periodicTextPosition="bottom"
 *      periodicTextFontSize="16px"
 *      periodicTextItalic={true}
 *    />
 * 
 * 4. Пунктирная линия с текстом:
 *    <TextPolyline
 *      positions={[[lat1, lng1], [lat2, lng2], [lat3, lng3]]}
 *      color="red"
 *      weight={2}
 *      dashArray={[5, 5]}
 *      dashOffset={2}
 *      segmentTexts={["100м", "200м"]}
 *      segmentTextColor="black"
 *      segmentTextFontSize="20px"
 *      segmentTextStroke={true}
 *      segmentTextStrokeColor="white"
 *    />
 * 
 * 5. Линия с обводкой:
 *    <TextPolyline
 *      positions={[[lat1, lng1], [lat2, lng2], [lat3, lng3]]}
 *      color="red"
 *      weight={3}
 *      strokeEnabled={true}
 *      strokeColor="white"
 *      strokeWeight={5}
 *      segmentTexts={["100м", "200м"]}
 *      segmentTextFontSize="14px"
 *      segmentTextBold={true}
 *    />
 * 
 * 6. Пунктирная линия с пунктирной обводкой:
 *    <TextPolyline
 *      positions={[[lat1, lng1], [lat2, lng2], [lat3, lng3]]}
 *      color="blue"
 *      weight={2}
 *      dashArray={[10, 5]}
 *      strokeEnabled={true}
 *      strokeColor="yellow"
 *      strokeWeight={4}
 *      strokeDashArray={[15, 10]}
 *      periodicText="ВЛ-110кВ"
 *      periodicTextSpacing={100}
 *      periodicTextFontSize="22px"
 *      periodicTextBold={true}
 *      periodicTextStroke={true}
 *      periodicTextStrokeColor="black"
 *    />
 * 
 * 7. Обратная совместимость (старые названия свойств):
 *    <TextPolyline
 *      positions={[[lat1, lng1], [lat2, lng2], [lat3, lng3]]}
 *      texts={["100м", "200м"]}
 *      textColor="green"
 *      textFontSize="18px"
 *      textBold={true}
 *    />
 * 
 * 8. Двухцветная линия (50/50):
 *    <TextPolyline
 *      positions={[[lat1, lng1], [lat2, lng2]]}
 *      weight={10}
 *      dualLineEnabled={true}
 *      color="blue"
 *      dualLineColor="red"
 *    />
 * 
 * 9. Двухцветная линия (70/30) с пунктиром:
 *    <TextPolyline
 *      positions={[[lat1, lng1], [lat2, lng2]]}
 *      weight={8}
 *      dashArray={[10, 5]}
 *      dualLineEnabled={true}
 *      dualLinePercentage={70}
 *      color="green"
 *      dualLineColor="orange"
 *      periodicText="Двойная линия"
 *    />
 * 
 * 10. Использование pane для управления порядком отображения:
 *    <TextPolyline
 *      positions={[[lat1, lng1], [lat2, lng2], [lat3, lng3]]}
 *      pane="fidersPane"
 *      color="blue"
 *      weight={4}
 *      segmentTexts={["100м", "200м"]}
 *      segmentTextColor="white"
 *      segmentTextFontSize="14px"
 *    />
 */